import { supabase } from '../lib/supabase.js';
import { state } from '../lib/state.js';

const TABLE = 'etoll_weekly_sections';
const BUTTON_CLASS = 'd24-etoll-weekly-toggle';
const ACTIVE_TEXT = 'AUTOMAT';
const INACTIVE_TEXT = 'AUTOMAT';

let observer = null;
let refreshTimer = 0;
let loadedRouteId = '';
let loadPromise = null;
let tableUnavailable = false;
const enabledBySection = new Map();

function sectionKey(routeId, sectionOrder) {
  return `${routeId || ''}:${Number(sectionOrder) || 0}`;
}

function setAppStatus(message, kind = 'neutral') {
  const status = document.getElementById('extensionStatus');
  if (!status) return;
  status.textContent = message;
  status.className = `status ${kind}`;
}

function sectionOrderFromCard(card) {
  const fromDataset = Number(card?.dataset?.sectionOrder || 0);
  if (fromDataset > 0) return fromDataset;
  const heading = String(card?.querySelector('h3')?.textContent || '');
  const match = heading.match(/^\s*(\d+)\s*\./);
  return match ? Number(match[1]) : null;
}

function currentEtollSection(sectionOrder) {
  return (state.currentRoute?.sections || []).find(section => (
    Number(section?.order) === Number(sectionOrder)
    && (section?.calculatorKey === 'etoll-pl' || section?.countryCode === 'PL')
  )) || null;
}

function setButtonState(button, enabled, { disabled = false, title = '' } = {}) {
  button.disabled = disabled;
  button.dataset.enabled = enabled ? 'true' : 'false';
  button.textContent = disabled && title === 'Automat niedostępny' ? 'NIEDOSTĘPNY' : (enabled ? ACTIVE_TEXT : INACTIVE_TEXT);
  button.className = `button small automation-toggle ${enabled ? 'is-active' : 'is-inactive'} ${BUTTON_CLASS}`;
  button.setAttribute('aria-pressed', enabled ? 'true' : 'false');
  if (title) button.title = title;
  else if (enabled) button.title = 'Oracle / e-TOLL: automat tygodniowy aktywny. Kliknij, aby go wyłączyć.';
  else button.title = 'Oracle / e-TOLL: włącz cotygodniowe automatyczne przeliczanie tego odcinka.';
}

async function loadCurrentRouteState(routeId) {
  if (!supabase || !routeId || !state.organizationId) {
    loadedRouteId = routeId || '';
    enabledBySection.clear();
    return;
  }

  if (loadedRouteId === routeId && loadPromise) return loadPromise;
  loadedRouteId = routeId;
  enabledBySection.clear();

  loadPromise = (async () => {
    const { data, error } = await supabase
      .from(TABLE)
      .select('section_order,enabled,last_enqueued_at,last_completed_at,last_error')
      .eq('organization_id', state.organizationId)
      .eq('route_id', routeId);

    if (error) {
      tableUnavailable = true;
      console.warn('[D24 ETOLL AUTO] Nie udało się odczytać listy automatu:', error);
      return;
    }

    tableUnavailable = false;
    for (const row of data || []) {
      enabledBySection.set(sectionKey(routeId, row.section_order), Boolean(row.enabled));
    }
  })().finally(() => {
    loadPromise = null;
  });

  return loadPromise;
}

async function toggleWeeklySection(sectionOrder, button) {
  const routeId = state.currentRoute?.id;
  const organizationId = state.organizationId;
  const section = currentEtollSection(sectionOrder);

  if (!supabase || !organizationId) {
    setAppStatus('Brak aktywnej sesji Supabase.', 'error');
    return;
  }
  if (!routeId || !section) {
    setAppStatus('Najpierw zapisz linię w Supabase.', 'warning');
    return;
  }

  const key = sectionKey(routeId, sectionOrder);
  const nextEnabled = !enabledBySection.get(key);
  button.disabled = true;
  button.textContent = nextEnabled ? 'WŁĄCZAM…' : 'WYŁĄCZAM…';

  const { data, error } = await supabase
    .from(TABLE)
    .upsert({
      organization_id: organizationId,
      route_id: routeId,
      section_order: Number(sectionOrder),
      enabled: nextEnabled,
      last_error: null,
    }, {
      onConflict: 'organization_id,route_id,section_order',
    })
    .select('section_order,enabled')
    .single();

  if (error) {
    console.error('[D24 ETOLL AUTO] Zapis automatu nie powiódł się:', error);
    button.disabled = false;
    setButtonState(button, Boolean(enabledBySection.get(key)));
    setAppStatus(`Nie udało się zmienić automatu e-TOLL: ${error.message}`, 'error');
    return;
  }

  const enabled = Boolean(data?.enabled);
  enabledBySection.set(key, enabled);
  setButtonState(button, enabled);
  setAppStatus(
    enabled
      ? 'e-TOLL: automat Oracle aktywny. Polski odcinek będzie przeliczany tygodniowo.'
      : 'e-TOLL: automat Oracle został wyłączony dla tego odcinka.',
    'ok'
  );
}

async function syncButtons() {
  const cards = [...document.querySelectorAll('#sectionsList .section-card[data-calculator="etoll-pl"]')];
  if (!cards.length) return;

  const routeId = state.currentRoute?.id || '';
  if (routeId !== loadedRouteId) await loadCurrentRouteState(routeId);
  else if (loadPromise) await loadPromise;

  for (const card of cards) {
    const actions = card.querySelector('.section-actions');
    if (!actions) continue;

    const sectionOrder = sectionOrderFromCard(card);
    if (!sectionOrder) continue;

    let button = actions.querySelector(`.${BUTTON_CLASS}`);
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.addEventListener('click', () => toggleWeeklySection(sectionOrder, button));
      actions.appendChild(button);
    }

    if (!routeId) {
      setButtonState(button, false, { disabled:true, title:'Najpierw zapisz linię' });
      continue;
    }

    if (tableUnavailable) {
      setButtonState(button, false, { disabled:true, title:'Automat niedostępny' });
      continue;
    }

    setButtonState(button, Boolean(enabledBySection.get(sectionKey(routeId, sectionOrder))));
  }
}

function scheduleSync() {
  window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    syncButtons().catch(error => console.warn('[D24 ETOLL AUTO] Błąd synchronizacji UI:', error));
  }, 30);
}

export function initEtollWeeklyUi() {
  if (observer) return;
  observer = new MutationObserver(scheduleSync);
  observer.observe(document.documentElement, { subtree:true, childList:true });
  window.addEventListener('focus', scheduleSync);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) scheduleSync(); });
  scheduleSync();
}
